import { useEffect, useState } from "react";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import {
  BanknotesIcon,
  BoltIcon,
  ChatBubbleLeftRightIcon,
  DocumentDuplicateIcon,
  PaperAirplaneIcon,
  RectangleGroupIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { cn } from "@utils";
import AiSparkleIcon from "../SmartNoteModal/AiSparkleIcon";

export const WHATS_NEW_SEEN_KEY = "smart-note-v2-whats-new-seen";

const CHANGES = [
  { icon: RectangleGroupIcon, title: "One view", body: "Read, chat and account side by side", tint: "bg-violet-100 text-violet-600" },
  { icon: BanknotesIcon, title: "Balance at a glance", body: "Overdue, this month, future", tint: "bg-rose-100 text-rose-600" },
  { icon: DocumentDuplicateIcon, title: "Every document", body: "Search, download, send in bulk", tint: "bg-sky-100 text-sky-600" },
  { icon: BoltIcon, title: "Live activity", body: "Updates as it happens", tint: "bg-amber-100 text-amber-600" },
  { icon: ChatBubbleLeftRightIcon, title: "History", body: "Pick up past conversations", tint: "bg-emerald-100 text-emerald-600" },
  { icon: PaperAirplaneIcon, title: "Quick actions", body: "Notes and payment requests in a click", tint: "bg-orange-100 text-orange-600" },
];

// Shown once, the first time someone opens the V2 modal, to introduce what changed from V1.
export default function WhatsNewModal({ isOpen, onClose }) {
  // Flipped a frame after opening so the tiles stagger in rather than appearing at once.
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    if (!isOpen) return setRevealed(false);
    const frame = requestAnimationFrame(() => setRevealed(true));
    return () => cancelAnimationFrame(frame);
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-[60]">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-zinc-900/80 backdrop-blur-sm transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
          <DialogPanel
            transition
            className="relative w-[600px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-3xl bg-white text-left shadow-2xl transition-all focus:outline-none data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <div className="relative overflow-hidden bg-gradient-to-br from-pink-500 via-rose-500 to-orange-400 px-8 pb-10 pt-9 text-center">
              <div aria-hidden="true" className="absolute -left-16 -top-16 size-48 rounded-full bg-white/20 blur-2xl" />
              <div aria-hidden="true" className="absolute -bottom-20 -right-10 size-56 rounded-full bg-yellow-200/30 blur-3xl" />

              <button
                type="button"
                onClick={onClose}
                className="absolute right-4 top-4 rounded-full p-1.5 text-white/70 transition-colors hover:bg-white/15 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
              >
                <span className="sr-only">Close</span>
                <XMarkIcon aria-hidden="true" className="size-5" />
              </button>

              <div className="relative mx-auto flex size-14 items-center justify-center rounded-2xl bg-white shadow-lg shadow-rose-900/20">
                <AiSparkleIcon className="size-8" />
              </div>
              <DialogTitle className="relative mt-5 text-3xl font-semibold tracking-tight text-white">
                Meet the new Ontik Intelligence
              </DialogTitle>
              <p className="relative mt-2 text-sm font-medium text-white/85">
                Everything on an account, in one place.
              </p>
            </div>

            <ul className="grid grid-cols-2 gap-3 p-6 sm:grid-cols-3">
              {CHANGES.map(({ icon: Icon, title, body, tint }, i) => (
                <li
                  key={title}
                  style={{ transitionDelay: revealed ? `${150 + i * 60}ms` : "0ms" }}
                  className={cn(
                    "rounded-2xl border border-zinc-100 bg-zinc-50/60 p-4 transition duration-500 ease-out",
                    revealed ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0",
                  )}
                >
                  <span className={cn("flex size-9 items-center justify-center rounded-xl", tint)}>
                    <Icon aria-hidden="true" className="size-5" />
                  </span>
                  <p className="mt-3 text-sm font-semibold text-zinc-900">{title}</p>
                  <p className="mt-0.5 text-xs text-zinc-500">{body}</p>
                </li>
              ))}
            </ul>

            <div className="px-6 pb-6">
              <button
                type="button"
                onClick={onClose}
                className="w-full rounded-xl bg-zinc-900 py-3 text-sm font-semibold text-white transition-colors hover:bg-zinc-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-zinc-900"
              >
                Let's go
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
